import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { ShieldCheck, Plus, TrendingUp, Users, BarChart3, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = ['prospect', 'pilot', 'signed', 'live', 'churned'];
const STAGE_COLORS = {
  prospect: 'bg-slate-100 text-slate-700',
  pilot:    'bg-amber-100 text-amber-800',
  signed:   'bg-blue-100 text-blue-800',
  live:     'bg-emerald-100 text-emerald-800',
  churned:  'bg-rose-100 text-rose-800',
};
const TIER_LABELS = {
  ma_essentials: 'MA Essentials · $4.50 PEPM',
  ma_advanced:   'MA Advanced · $8.50 PEPM',
  ma_premium:    'MA Premium · $14 PEPM',
};

const fmtUSD = (n) => {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/**
 * /admin/payer-vbc — Payer VBC Contract Dashboard (Blueprint Decision #29).
 *
 * Admin surface for the Layer-6 PEPM revenue stream. CRUD on payer
 * contracts + live portfolio aggregates (total ARR, live vs pipeline,
 * tier mix, stage mix).
 */
export default function AdminPayerVbc() {
  const { getAuthHeader } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    payer_name: '', payer_state: '', tier: 'ma_advanced', managed_lives: 50000, stage: 'prospect',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contractsRes, portRes] = await Promise.all([
        axios.get(`${API}/admin/payer-contracts`, getAuthHeader()),
        axios.get(`${API}/admin/payer-vbc/portfolio`, getAuthHeader()),
      ]);
      setContracts(contractsRes.data.contracts || []);
      setPortfolio(portRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load payer dashboard');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const createContract = async (e) => {
    e.preventDefault();
    if (!form.payer_name || form.payer_name.length < 2) {
      toast.error('Payer name is required'); return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        managed_lives: Number(form.managed_lives),
        payer_state: form.payer_state ? form.payer_state.toUpperCase() : null,
      };
      await axios.post(`${API}/admin/payer-contracts`, payload, getAuthHeader());
      toast.success(`Contract created — ${fmtUSD(Number(form.managed_lives) * (form.tier === 'ma_premium' ? 14 : form.tier === 'ma_advanced' ? 8.5 : 4.5) * 12)} ARR`);
      setShowCreate(false);
      setForm({ payer_name: '', payer_state: '', tier: 'ma_advanced', managed_lives: 50000, stage: 'prospect' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create contract');
    } finally {
      setCreating(false);
    }
  };

  const advanceStage = async (id, currentStage) => {
    const next = STAGES[Math.min(STAGES.length - 2, STAGES.indexOf(currentStage) + 1)];
    if (next === currentStage) return;
    try {
      await axios.patch(`${API}/admin/payer-contracts/${id}`, { stage: next }, getAuthHeader());
      toast.success(`Advanced to ${next}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const archive = async (id) => {
    if (!window.confirm('Archive this contract?')) return;
    try {
      await axios.delete(`${API}/admin/payer-contracts/${id}`, getAuthHeader());
      toast.success('Contract archived'); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed');
    }
  };

  return (
    <DashboardShell data-testid="payer-vbc-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cn-eyebrow inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Layer 6 · Payer Value-Based Care
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Payer VBC portfolio</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            PEPM contracts on managed Medicare Advantage / Medicaid LTSS lives — the path from $100M to $1B+ ARR.
          </p>
        </div>
        <Button onClick={() => setShowCreate((s) => !s)} className="bg-cn-primary text-white hover:bg-cn-primary-hover" data-testid="payer-create-toggle">
          <Plus className="w-4 h-4 mr-2" /> New contract
        </Button>
      </header>

      {/* PORTFOLIO TILES */}
      {portfolio && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="portfolio-tiles">
          <Tile label="Live ARR"     value={fmtUSD(portfolio.live_arr)}     icon={<TrendingUp className="w-4 h-4 text-emerald-600" />} testid="tile-live-arr" />
          <Tile label="Pipeline ARR" value={fmtUSD(portfolio.pipeline_arr)} icon={<BarChart3 className="w-4 h-4 text-amber-600" />}  testid="tile-pipeline-arr" />
          <Tile label="Managed lives" value={Number(portfolio.total_managed_lives).toLocaleString()} icon={<Users className="w-4 h-4 text-cn-primary" />} testid="tile-lives" />
          <Tile label="Contracts" value={portfolio.total_contracts} icon={<ShieldCheck className="w-4 h-4 text-slate-700" />} testid="tile-count" />
        </div>
      )}

      {/* CREATE FORM */}
      {showCreate && (
        <form onSubmit={createContract} className="rounded-2xl bg-white border border-slate-100 p-5 mb-6" data-testid="create-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Payer name" required testid="form-name" value={form.payer_name} onChange={(v) => setForm({ ...form, payer_name: v })} />
            <Input label="State (2-letter)" testid="form-state" value={form.payer_state} onChange={(v) => setForm({ ...form, payer_state: v.toUpperCase().slice(0, 2) })} />
            <Select label="Tier" testid="form-tier" value={form.tier} onChange={(v) => setForm({ ...form, tier: v })}
              options={[
                { v: 'ma_essentials', l: 'MA Essentials · $4.50 PEPM' },
                { v: 'ma_advanced',   l: 'MA Advanced · $8.50 PEPM' },
                { v: 'ma_premium',    l: 'MA Premium · $14 PEPM' },
              ]} />
            <Input label="Managed lives" type="number" testid="form-lives" value={form.managed_lives} onChange={(v) => setForm({ ...form, managed_lives: v })} />
            <Select label="Stage" testid="form-stage" value={form.stage} onChange={(v) => setForm({ ...form, stage: v })}
              options={STAGES.map((s) => ({ v: s, l: s }))} />
          </div>
          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={creating} className="bg-cn-primary text-white hover:bg-cn-primary-hover" data-testid="create-submit">
              {creating ? 'Creating…' : 'Create contract'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      )}

      {/* CONTRACT TABLE */}
      <section className="rounded-2xl bg-white border border-slate-100 overflow-hidden" data-testid="contracts-table">
        {loading ? (
          <div className="p-8 text-slate-500">Loading contracts…</div>
        ) : contracts.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">No payer contracts yet.</p>
            <p className="text-sm text-slate-500 mt-1">Click "New contract" to add the first one.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold">Payer</th>
                <th className="text-left p-3 font-semibold">Tier</th>
                <th className="text-right p-3 font-semibold">Lives</th>
                <th className="text-right p-3 font-semibold">Annual ARR</th>
                <th className="text-left p-3 font-semibold">Stage</th>
                <th className="text-right p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{c.payer_name}</div>
                    {c.payer_state && <div className="text-xs text-slate-500">{c.payer_state}</div>}
                  </td>
                  <td className="p-3 text-slate-700">{TIER_LABELS[c.tier]}</td>
                  <td className="p-3 text-right tabular-nums">{Number(c.managed_lives).toLocaleString()}</td>
                  <td className="p-3 text-right tabular-nums font-semibold text-cn-primary">{fmtUSD(c.annual_arr)}</td>
                  <td className="p-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${STAGE_COLORS[c.stage]}`}>
                      {c.stage}
                    </span>
                  </td>
                  <td className="p-3 text-right space-x-2">
                    {c.stage !== 'live' && c.stage !== 'churned' && (
                      <Button size="sm" variant="outline" onClick={() => advanceStage(c.id, c.stage)} data-testid={`advance-${c.id}`}>
                        Advance →
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => archive(c.id)} data-testid={`archive-${c.id}`} className="text-rose-600 hover:text-rose-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </DashboardShell>
  );
}

function Tile({ label, value, icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', required, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={`input-${testid}`}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-cn-primary"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`input-${testid}`}
        className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-cn-primary"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
